import React, { useState, useEffect } from 'react';

/**
 * カラムタイプ定数
 * JavaのgetSpSource関数で使用されるカラムタイプと対応
 */
const COLUMN_TYPES = {
  LABEL: 1,           // ラベル（セクション区切り）
  TEXT_SINGLE: 2,     // 文字列(1行)
  TEXT_MULTI: 3,      // 文字列(複数行)
  NUMBER: 4,          // 数値
  RADIO: 5,           // ラジオボタン
  CHECKBOX: 6,        // チェックボックス
  MULTI_SELECT: 7,    // 複数選択
  DROPDOWN: 8,        // プルダウン
  DATE: 9,            // 日付
  DATETIME: 10,       // 日時
  SCALE_2: 11,        // 2段階選択
  SCALE_3: 12,        // 3段階選択
  SCALE_5: 13,        // 5段階選択
  SCALE_10: 14,       // 10段階選択
  DECIMAL: 17         // 小数点数値
};

/**
 * 撮影メモフォームのスタイル定義
 * 編集可能版と読み取り専用版の両方のスタイルを含む
 */
const styles = {
    // フォームラベル（セクション区切り用）
    formLabel: {
        fontSize: '14px',
        fontWeight: 'bold',
        marginBottom: '10px',
        color: '#333',
        backgroundColor: '#FFE4E1',
        padding: '5px 10px',
        borderRadius: '3px'
    },
    // カラムコンテナ
    column: {
        marginBottom: '15px'
    },
    // フィールドタイトル
    title: {
        fontSize: '14px',
        fontWeight: 'bold',
        marginBottom: '8px',
        color: '#333'
    },
    // 値エリアコンテナ
    value: {
        marginBottom: '10px'
    },
    // 入力フィールド（テキスト、数値、日付等）
    input: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '5px',
        fontSize: '14px',
        backgroundColor: 'white',
        boxSizing: 'border-box'
    },
    // テキストエリア（複数行テキスト）
    textarea: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '5px',
        fontSize: '14px',
        resize: 'vertical',
        minHeight: '80px',
        backgroundColor: 'white',
        boxSizing: 'border-box'
    },
    // セレクトボックス（プルダウン、複数選択）
    select: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '5px',
        fontSize: '14px',
        backgroundColor: 'white',
        boxSizing: 'border-box'
    },
    // ラジオボタングループコンテナ
    radioGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px'
    },
    // ラジオボタン（編集可能）
    radio: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#ddd',
        borderRadius: '5px',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    // ラジオボタン選択時（編集可能）
    radioChecked: {
        backgroundColor: '#FDD',
        borderColor: '#FAA'
    },
    // チェックボックス（編集可能）
    checkbox: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#ddd',
        borderRadius: '5px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        marginBottom: '5px'
    },
    // チェックボックス選択時（編集可能）
    checkboxChecked: {
        backgroundColor: '#FDD',
        borderColor: '#FAA'
    },
    // 読み取り専用フィールド（テキスト、数値等）
    readOnlyField: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '5px',
        fontSize: '14px',
        backgroundColor: '#f5f5f5',
        color: '#666',
        boxSizing: 'border-box',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'default'
    },
    // ラジオボタン（読み取り専用）
    radioReadOnly: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#ddd',
        borderRadius: '5px',
        backgroundColor: '#f5f5f5',
        color: '#666',
        transition: 'all 0.2s ease',
        cursor: 'default'
    },
    // ラジオボタン選択時（読み取り専用）
    radioCheckedReadOnly: {
        backgroundColor: '#C8C8C8',
        borderColor: '#AAA'
    },
    // チェックボックス（読み取り専用）
    checkboxReadOnly: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#ddd',
        borderRadius: '5px',
        backgroundColor: '#f5f5f5',
        color: '#666',
        transition: 'all 0.2s ease',
        marginBottom: '5px',
        cursor: 'default'
    },
    // チェックボックス選択時（読み取り専用）
    checkboxCheckedReadOnly: {
        backgroundColor: '#C8C8C8',
        borderColor: '#AAA'
    },
    // テキストエリア（読み取り専用）
    textareaReadOnly: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '5px',
        fontSize: '14px',
        resize: 'vertical',
        minHeight: '80px',
        backgroundColor: '#f5f5f5',
        boxSizing: 'border-box',
        color: '#666',
        display: 'flex',
        alignItems: 'center',
        cursor: 'default'
    }
}

/**
 * 撮影メモフォームコンポーネント
 * JavaのgetSpSource関数のロジックをReactで実装
 * 編集可能版と閲覧専用版の両方をサポート
 * 
 * @param {string} objectId - オブジェクトID
 * @param {Array} satsueiMemoColumns - 撮影メモカラム定義配列
 * @param {Function} onChange - フォームデータ変更時のコールバック
 * @param {Function} onFormChange - フォーム変更フラグ変更時のコールバック
 * @param {string} className - CSSクラス名
 * @param {Function} onRef - refを通じて親コンポーネントにメソッドを公開するコールバック
 * @param {boolean} readOnly - 閲覧専用フラグ
 */
const SatsueiMemoForm = ({ 
  objectId, 
  satsueiMemoColumns = [], 
  onChange = () => {},
  onFormChange = () => {},
  className = "",
  onRef = () => {},
  readOnly = false
}) => {
  // フォームデータの状態管理
  const [formData, setFormData] = useState({}); // フォームの入力値
  const [changeFlg, setChangeFlg] = useState(false); // フォーム変更フラグ

  /**
   * フォームデータの初期化処理
   * カラムタイプに応じて適切な初期値を設定
   */
  const initializeFormData = () => {
    const initialData = {};
    satsueiMemoColumns.forEach(column => {
      switch (column.columnType) {
        case COLUMN_TYPES.LABEL:
          break;
        case COLUMN_TYPES.TEXT_SINGLE:
        case COLUMN_TYPES.TEXT_MULTI:
        case COLUMN_TYPES.NUMBER:
        case COLUMN_TYPES.DATE:
        case COLUMN_TYPES.DATETIME:
        case COLUMN_TYPES.DECIMAL:
          initialData[column.key] = column.value || column.initialValue || '';
          break;
        case COLUMN_TYPES.RADIO:
        case COLUMN_TYPES.CHECKBOX:
        case COLUMN_TYPES.MULTI_SELECT:
        case COLUMN_TYPES.DROPDOWN:
        case COLUMN_TYPES.SCALE_2:
        case COLUMN_TYPES.SCALE_3:
        case COLUMN_TYPES.SCALE_5:
        case COLUMN_TYPES.SCALE_10:
          initialData[column.key] = {};
          column.satsueiMemoColumnValueList?.forEach(columnValue => {
            initialData[column.key][columnValue.key] = columnValue.value;
          });
          break;
      }
    });
    setFormData(initialData);
    setChangeFlg(false);
    onChange(initialData);
  };

  /**
   * フォーム値の変更処理
   * @param {string} columnKey - カラムキー
   * @param {string} valueKey - 値キー（選択肢系の場合）
   * @param {any} value - 新しい値
   */
  const handleValueChange = (columnKey, valueKey, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (valueKey) {
        newData[columnKey] = { ...newData[columnKey], [valueKey]: value };
      } else {
        newData[columnKey] = value;
      }
      return newData;
    });
    setChangeFlg(true);
  };

  // 撮影メモカラム定義が変更された時にフォームデータを初期化
  useEffect(() => {
    initializeFormData();
  }, [satsueiMemoColumns]); // eslint-disable-line react-hooks/exhaustive-deps

  // refを通じて親コンポーネントから初期化メソッドを呼び出せるようにする
  useEffect(() => {
    if (typeof onRef === 'function') {
      onRef({
        initializeFormData
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // フォームデータの変更を監視して親コンポーネントに通知
  useEffect(() => {
    onChange(formData);
    if (changeFlg) {
      onFormChange(true);
    }
  }, [formData, changeFlg, onChange, onFormChange]);

  /**
   * フォーム要素のレンダリング処理
   * カラムタイプに応じて適切なフォーム要素を生成
   * @param {Object} column - カラム定義オブジェクト
   * @returns {JSX.Element} レンダリングされたフォーム要素
   */
  const renderFormElement = (column) => {
    const key = column.key;

    switch (column.columnType) {
      case COLUMN_TYPES.LABEL:
        return (
          <li key={key} className="formLabel" style={styles.formLabel}>
            {column.satsueiMemoColumnName}
          </li>
        );

      case COLUMN_TYPES.TEXT_SINGLE: {
         const textValue = formData[key] || column.value || column.initialValue || '';
         return (
           <li key={key} style={styles.column}>
             <div style={styles.title}>
               {column.satsueiMemoColumnName}
               {column.requiredFlg !== 0 && ' *'}
             </div>
             <div style={styles.value}>
               {readOnly ? (
                 <div style={styles.readOnlyField}>
                   {textValue}
                 </div>
               ) : (
                 <input
                   type="text"
                   id={key}
                   className="change"
                   value={textValue}
                   maxLength="3000"
                   style={styles.input}
                   onChange={(e) => handleValueChange(key, null, e.target.value)}
                   data-testid={`input-${key}`}
                 />
               )}
             </div>
           </li>
         );
      }

      case COLUMN_TYPES.TEXT_MULTI: {
        const textareaValue = formData[key] || column.value || column.initialValue || '';
        return (
          <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div style={styles.value}>
              {readOnly ? (
                <textarea
                  id={key}
                  rows="4"
                  maxLength="3000"
                  value={textareaValue}
                  style={styles.textareaReadOnly}
                  disabled={true}
                />
              ) : (
                <textarea
                  id={key}
                  className="change"
                  rows="4"
                  maxLength="3000"
                  value={textareaValue}
                  style={styles.textarea}
                  onChange={(e) => handleValueChange(key, null, e.target.value)}
                  data-testid={`textarea-${key}`}
                />
              )}
            </div>
          </li>
        );
      }

      case COLUMN_TYPES.NUMBER: {
        const numberValue = formData[key] || column.value || column.initialValue || '';
        return (
          <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div style={styles.value}>
              {readOnly ? (
                <div style={styles.readOnlyField}>
                  {numberValue}
                </div>
              ) : (
                <input
                  type="number"
                  id={key}
                  className="change"
                  value={numberValue}
                  style={styles.input}
                  onChange={(e) => handleValueChange(key, null, e.target.value)}
                  data-testid={`number-${key}`}
                />
              )}
            </div>
          </li>
        );
      }

      case COLUMN_TYPES.DECIMAL: {
        const decimalValue = formData[key] || column.value || column.initialValue || '';
        return (
          <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div style={styles.value}>
              {readOnly ? (
                <div style={styles.readOnlyField}>
                  {decimalValue}
                </div>
              ) : (
                <input
                  type="number"
                  step="0.1"
                  id={key}
                  className="change"
                  value={decimalValue}
                  style={styles.input}
                  onChange={(e) => handleValueChange(key, null, e.target.value)}
                  data-testid={`number-${key}`}
                />
              )}
            </div>
          </li>
        );
      }

      case COLUMN_TYPES.RADIO:
        return (
        <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div style={styles.value}>
              <div style={styles.radioGroup}>
                {column.satsueiMemoColumnValueList?.map(columnValue => {
                  const isChecked = formData[key]?.[columnValue.key] === '1' ? true : false;
                  
                  if (readOnly) {
                    const labelStyle = {
                      ...styles.radioReadOnly,
                      ...(isChecked ? styles.radioCheckedReadOnly : {})
                    };
                    
                    return (
                      <div key={columnValue.key} style={{ display: columnValue.deleteFlg === 1 ? 'none' : 'block' }}>
                        <div style={labelStyle}>
                          {columnValue.satsueiMemoColumnValueName}
                        </div>
                      </div>
                    );
                  } else {
                    const labelStyle = {
                      ...styles.radio,
                      ...(isChecked ? styles.radioChecked : {})
                    };
                    
                    return (
                      <div key={columnValue.key} style={{ display: columnValue.deleteFlg === 1 ? 'none' : 'block' }}>
                        <label
                          htmlFor={columnValue.key}
                          id={`label_${columnValue.key}`}
                          style={labelStyle}
                          className={isChecked ? 'radio-checked' : ''}
                        >
                          <input
                            type="radio"
                            name={key}
                            id={columnValue.key}
                            className="change"
                            value={columnValue.satsueiMemoColumnValueId.toString()}
                            checked={isChecked}
                            onChange={() => {
                              handleValueChange(key, columnValue.key, '1');
                              // 他のラジオボタンをクリア
                              setFormData(prev => {
                                const newData = { ...prev };
                                if (newData[key]) {
                                  Object.keys(newData[key]).forEach(k => {
                                    if (k !== columnValue.key) {
                                      newData[key][k] = '0';
                                    }
                                  });
                                }
                                return newData;
                              });
                            }}
                            style={{ display: 'none' }}
                            data-testid={`radio-${key}-${columnValue.key}`}
                          />
                          {columnValue.satsueiMemoColumnValueName}
                        </label>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </li>
        );

      case COLUMN_TYPES.CHECKBOX:
        return (
        <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div style={styles.value}>
              {column.satsueiMemoColumnValueList?.map(columnValue => {
                const isChecked = formData[key]?.[columnValue.key] === '1' ? true : false;
                
                if (readOnly) {
                  return (
                    <div
                      key={columnValue.key}
                      style={{
                        ...styles.checkboxReadOnly,
                        ...(isChecked ? styles.checkboxCheckedReadOnly : {}),
                        display: columnValue.deleteFlg === 1 ? 'none' : 'flex'
                      }}
                    >
                      {columnValue.satsueiMemoColumnValueName}
                    </div>
                  );
                } else {
                  return (
                    <label
                      key={columnValue.key}
                      htmlFor={columnValue.key}
                      id={`label_${columnValue.key}`}
                      style={{
                        ...styles.checkbox,
                        ...(isChecked ? styles.checkboxChecked : {}),
                        display: columnValue.deleteFlg === 1 ? 'none' : 'flex'
                      }}
                    >
                      <input
                        type="checkbox"
                        id={columnValue.key}
                        className="change"
                        value={columnValue.satsueiMemoColumnValueId.toString()}
                        checked={isChecked}
                        onChange={(e) => handleValueChange(key, columnValue.key, e.target.checked ? '1' : '0')}
                        style={{ display: 'none' }}
                        data-testid={`checkbox-${key}-${columnValue.key}`}
                      />
                      {columnValue.satsueiMemoColumnValueName}
                    </label>
                  );
                }
              })}
            </div>
          </li>
        );

      case COLUMN_TYPES.MULTI_SELECT: {
        // 選択された値を取得
        const selectedValues = [];
        const selectedValueNames = [];
        column.satsueiMemoColumnValueList?.forEach(columnValue => {
          if (formData[key]?.[columnValue.key] === '1') {
            selectedValues.push(columnValue.satsueiMemoColumnValueId.toString());
            selectedValueNames.push(columnValue.satsueiMemoColumnValueName);
          }
        });
        
        return (
        <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div className="value">
              {readOnly ? (
                <div style={styles.readOnlyField}>
                  {selectedValueNames.length > 0 ? selectedValueNames.join(', ') : '未選択'}
                </div>
              ) : (
                <select
                  id={key}
                  name={key}
                  data-testid={`multiple-select-${key}`}
                  className="change"
                  multiple
                  size="1"
                  style={styles.select}
                  value={selectedValues}
                  onChange={(e) => {
                    const selectedValues = Array.from(e.target.selectedOptions).map(option => option.value);
                    const newData = { ...formData };
                    newData[key] = {};
                    column.satsueiMemoColumnValueList?.forEach(columnValue => {
                      newData[key][columnValue.key] = selectedValues.includes(columnValue.satsueiMemoColumnValueId.toString()) ? '1' : '0';
                    });
                    setFormData(newData);
                    setChangeFlg(true);
                  }}
                >
                  {column.satsueiMemoColumnValueList?.map(columnValue => {
                    if (columnValue.deleteFlg === 1) {
                      return (
                        <input
                          key={columnValue.key}
                          type="hidden"
                          id={columnValue.key}
                          className="change"
                          value={columnValue.value === '1' ? 'true' : 'false'}
                        />
                      );
                    }
                    
                    return (
                      <option
                        key={columnValue.key}
                        id={columnValue.key}
                        className="change"
                        value={columnValue.satsueiMemoColumnValueId.toString()}
                        data-testid={`multiple-select-${key}-${columnValue.key}`}
                      >
                        {columnValue.satsueiMemoColumnValueName}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          </li>
        );
      }

      case COLUMN_TYPES.DROPDOWN: {
        // 選択された値を取得
        let selectedValue = "0"; // デフォルトは未指定
        let selectedValueName = "未選択";
        
        // 選択された値を探す
        const selectedColumnValue = column.satsueiMemoColumnValueList?.find(columnValue => {
          return formData[key]?.[columnValue.key] === '1';
        });
        
        if (selectedColumnValue) {
          selectedValue = selectedColumnValue.satsueiMemoColumnValueId.toString();
          selectedValueName = selectedColumnValue.satsueiMemoColumnValueName;
        }
        
        return (
        <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div className="value">
              {readOnly ? (
                <div style={styles.readOnlyField}>
                  {selectedValueName}
                </div>
              ) : (
                <select
                  id={key}
                  name={key}
                  className="change"
                  style={styles.select}
                  value={selectedValue}
                  onChange={(e) => {
                    const newData = { ...formData };
                    newData[key] = {};
                    column.satsueiMemoColumnValueList?.forEach(columnValue => {
                      newData[key][columnValue.key] = e.target.value === columnValue.satsueiMemoColumnValueId.toString() ? '1' : '0';
                    });
                    setFormData(newData);
                    setChangeFlg(true);
                  }}
                  data-testid={`select-${key}`}
                >
                  <option id="0" value="0">未指定</option>
                  {column.satsueiMemoColumnValueList?.map(columnValue => {
                    return (
                      <option
                        key={columnValue.key}
                        id={columnValue.key}
                        className="change"
                        value={columnValue.satsueiMemoColumnValueId.toString()}
                        style={{ display: columnValue.deleteFlg === 1 ? 'none' : 'block' }}
                      >
                        {columnValue.satsueiMemoColumnValueName}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          </li>
        );
      }

      case COLUMN_TYPES.DATE: {
        const currentDateValue = formData[key] || column.value || column.initialValue || '';
        const dateValue = currentDateValue ? currentDateValue.replace(/\//g, '-') : '';
        // 読み取り専用の場合は「yyyy/MM/dd」形式で表示
        const displayDateValue = currentDateValue ? currentDateValue : '';
        return (
          <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div style={styles.value}>
              {readOnly ? (
                <div style={styles.readOnlyField}>
                  {displayDateValue}
                </div>
              ) : (
                <input
                  type="date"
                  id={key}
                  className="change"
                  value={dateValue}
                  style={styles.input}
                  onChange={(e) => handleValueChange(key, null, e.target.value)}
                  data-testid={`date-${key}`}
                />
              )}
            </div>
          </li>
        );
      }

      case COLUMN_TYPES.DATETIME: {
        const currentDatetimeValue = formData[key] || column.value || column.initialValue || '';
        const datetimeValue = currentDatetimeValue ?
          currentDatetimeValue.replace(/\//g, '-').replace(' ', 'T') : '';
        // 読み取り専用の場合は「yyyy/MM/dd HH:mm」形式で表示
        const displayDatetimeValue = currentDatetimeValue ? currentDatetimeValue : '';
        return (
          <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div style={styles.value}>
              {readOnly ? (
                <div style={styles.readOnlyField}>
                  {displayDatetimeValue}
                </div>
              ) : (
                <input
                  type="datetime-local"
                  id={key}
                  className="change"
                  value={datetimeValue}
                  style={styles.input}
                  onChange={(e) => handleValueChange(key, null, e.target.value)}
                  data-testid={`datetime-${key}`}
                />
              )}
            </div>
          </li>
        );
      }

      case COLUMN_TYPES.SCALE_2:
      case COLUMN_TYPES.SCALE_3:
      case COLUMN_TYPES.SCALE_5:
      case COLUMN_TYPES.SCALE_10:
        return (
        <li key={key} style={styles.column}>
            <div style={styles.title}>
              {column.satsueiMemoColumnName}
              {column.requiredFlg !== 0 && ' *'}
            </div>
            <div style={styles.value}>
              <div style={styles.radioGroup}>
                {column.satsueiMemoColumnValueList?.map(columnValue => {
                  const isChecked = formData[key]?.[columnValue.key] === '1' ? true : false;
                  
                  if (readOnly) {
                    return (
                      <div key={columnValue.key} style={{ display: columnValue.deleteFlg === 1 ? 'none' : 'block' }}>
                        <div
                          style={{
                            ...styles.radioReadOnly,
                            ...(isChecked ? styles.radioCheckedReadOnly : {})
                          }}
                        >
                          {columnValue.satsueiMemoColumnValueName}
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div key={columnValue.key} style={{ display: columnValue.deleteFlg === 1 ? 'none' : 'block' }}>
                        <label
                          htmlFor={columnValue.key}
                          id={`label_${columnValue.key}`}
                          style={{
                            ...styles.radio,
                            ...(isChecked ? styles.radioChecked : {})
                          }}
                        >
                          <input
                            type="radio"
                            name={key}
                            id={columnValue.key}
                            className="change"
                            value={columnValue.satsueiMemoColumnValueId.toString()}
                            checked={isChecked}
                            onChange={() => {
                              handleValueChange(key, columnValue.key, '1');
                              // 他のラジオボタンをクリア
                              setFormData(prev => {
                                const newData = { ...prev };
                                if (newData[key]) {
                                  Object.keys(newData[key]).forEach(k => {
                                    if (k !== columnValue.key) {
                                      newData[key][k] = '0';
                                    }
                                  });
                                }
                                return newData;
                              });
                            }}
                            style={{ display: 'none' }}
                            data-testid={`radio-${key}-${columnValue.key}`}
                          />
                          {columnValue.satsueiMemoColumnValueName}
                        </label>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </li>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`satsueiMemoForm ${className}`} data-testid="satsuei-memo-form">
      <input type="hidden" id="objectId" value={objectId} />
      <ul className="satsueiMemoForm">
        {satsueiMemoColumns.map(column => renderFormElement(column))}
      </ul>
    </div>
  );
};

export default SatsueiMemoForm;
